/**
 * Admin — Revenue Calculation, the FIRST ORDER VALUE reconciliation panel (card 2844).
 *
 * Sibling of `RevenueReconciliationPanel` (STORED ECONOMICS / legacy columns), which this file
 * deliberately does not touch. The API already adjudicates every question rendered here and
 * publishes the verdict on `reconciliation.firstOrderValue`.
 *
 * ⛔ NO RECOMPUTATION IN THE CLIENT. Agreement, pair findings, discrepancy counts and the
 *   renewals-excluded check are all read straight off the payload. The whole point of the
 *   endpoint is that the SERVER adjudicates; a view that re-derived the verdict could disagree
 *   with the thing it is supposed to be reporting.
 *
 * 🪤 AN ABSENT PANEL MUST NOT READ AS A PASSING ONE. Same house rule the page states at
 *   `RevenueCalculationPage.jsx:80-81` for its own empty regions: when the key is missing we
 *   render an explicit DID NOT RUN state, never silence and never a green verdict.
 */

import styles from '../../pages/admin/RevenueCalculationPage.module.css';
import { deriveFovVerdict } from '../../utils/fovReconciliation';

/** Pure. The renewals-excluded cross-check — an object, not a scalar. */
const RenewalsExcluded = ({ renewalsExcluded }) => {
  const { counts = [], distinctValues = [], agrees, checkedCount = 0, unresolvedCount = 0 } =
    renewalsExcluded ?? {};
  if (counts.length === 0) return null;
  return (
    <>
      <h3 className={styles.reconGroupTitle}>Renewals excluded</h3>
      <p
        className={agrees ? styles.reconVerdictPass : styles.reconVerdictFail}
        data-testid="fov-recon-renewals-verdict"
        data-agrees={agrees === true ? 'true' : 'false'}
      >
        {agrees
          ? `All ${checkedCount} surfaces exclude the same ${distinctValues.join(' / ')} renewal order${distinctValues[0] === 1 ? '' : 's'}.`
          : `${checkedCount} surfaces do not agree on how many renewals were excluded (${distinctValues.join(', ')}); ${unresolvedCount} unresolved.`}
      </p>
      <ul className={styles.reconList} data-testid="fov-recon-renewals">
        {counts.map((row) => (
          <li key={row.key} className={styles.reconRowPass} data-testid={`fov-recon-renewal-${row.key}`}>
            <div className={styles.reconHead}>
              <span className={styles.reconLabel}>{row.surface}</span>
              <span className={styles.reconPopulation}>
                {row.renewalsExcluded} excluded
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

/** Pure. One publisher — a surface that publishes a First Order Value reading. */
const PublisherRow = ({ publisher, reading }) => (
  <li className={styles.reconRowPass} data-testid={`fov-recon-publisher-${publisher.key}`}>
    <div className={styles.reconHead}>
      <span
        className={reading?.present ? styles.reconBadgePass : styles.reconBadgeFail}
        data-testid={`fov-recon-publisher-status-${publisher.key}`}
      >
        {reading?.present ? 'PUBLISHES' : 'MISSING'}
      </span>
      <span className={styles.reconLabel}>{publisher.label}</span>
      <span className={styles.reconPopulation}>
        {publisher.aggregates ? 'aggregate' : 'per order'}
      </span>
    </div>
    <dl className={styles.reconGrid}>
      <dt className={styles.reconTerm}>Reads</dt>
      <dd className={styles.reconDef}>{publisher.reads}</dd>
      <dt className={styles.reconTerm}>Source</dt>
      <dd className={styles.reconDefMono}>{publisher.source}</dd>
    </dl>
  </li>
);

/** Pure. One compared pair. `findings` empty ⇒ the two surfaces matched. */
const PairRow = ({ pair }) => {
  const findings = pair.findings ?? [];
  const ok = findings.length === 0;
  return (
    <li
      className={ok ? styles.reconRowPass : styles.reconRowFail}
      data-testid={`fov-recon-pair-${pair.leftKey}--${pair.rightKey}`}
      data-status={ok ? 'PASS' : 'FAIL'}
    >
      <div className={styles.reconHead}>
        <span className={ok ? styles.reconBadgePass : styles.reconBadgeFail}>
          {ok ? 'PASS' : 'FAIL'}
        </span>
        <span className={styles.reconLabel}>{pair.leftSurface} ↔ {pair.rightSurface}</span>
      </div>
      {findings.length > 0 && (
        <ul className={styles.reconCauses}>
          {findings.map((finding, i) => (
            <li key={`${pair.leftKey}-${pair.rightKey}-${i}`} className={styles.reconCause}>
              {typeof finding === 'string' ? finding : JSON.stringify(finding)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

/** Pure. The panel. */
const RevenueFovReconciliationPanel = ({ firstOrderValue }) => {
  const verdict = deriveFovVerdict(firstOrderValue);

  if (verdict.state === 'did-not-run') {
    return (
      <div className={styles.recon} data-testid="fov-reconciliation-panel" data-state="did-not-run">
        <h3 className={styles.reconGroupTitle}>First Order Value reconciliation</h3>
        <p className={styles.reconVerdictFail} data-testid="fov-recon-verdict" data-state="did-not-run">
          DID NOT RUN — the reconciliation endpoint published no <code>firstOrderValue</code> block.
        </p>
        <p className={styles.emptyNote}>
          This panel is the page’s own self-test for First Order Value, so an absent panel does not
          mean the publishers agree — it means the check did not run. Nothing below has been graded.
        </p>
      </div>
    );
  }

  const {
    publishers = [], readings = {}, pairs = [], pairCount = 0, publisherCount = 0,
    discrepancies = [], discrepancyCount = 0, missingPublishers = [],
    renewalsExcluded = null, basis = null, label = 'First Order Value',
    observedFirstOrderValueVersionsText = null, window: fovWindow = null
  } = firstOrderValue;

  return (
    <div
      className={styles.recon}
      data-testid="fov-reconciliation-panel"
      data-state={verdict.state}
    >
      <h3 className={styles.reconGroupTitle}>
        {/* Card 28313. The ROWS' versions, worded exactly as `RevenueFirstOrderValueRow`
            words them, so a reader sees one reading twice instead of two numbers that
            disagree. ⛔ Never the engine constant. */}
        {label} reconciliation
        {observedFirstOrderValueVersionsText
          ? ` · version ${observedFirstOrderValueVersionsText}`
          : ''}
      </h3>

      {verdict.state === 'vacuous' ? (
        <p className={styles.reconVerdictFail} data-testid="fov-recon-verdict" data-state="vacuous">
          RAN BUT COMPARED NOTHING — {publisherCount} publisher
          {publisherCount === 1 ? '' : 's'}, {pairCount} pair{pairCount === 1 ? '' : 's'}. A check
          with nothing to compare cannot pass.
        </p>
      ) : (
        <p
          className={verdict.state === 'agrees' ? styles.reconVerdictPass : styles.reconVerdictFail}
          data-testid="fov-recon-verdict"
          data-state={verdict.state}
          data-agrees={verdict.state === 'agrees' ? 'true' : 'false'}
          data-pair-count={pairCount}
          data-publisher-count={publisherCount}
          data-discrepancy-count={discrepancyCount}
        >
          {verdict.state === 'agrees'
            ? `All ${publisherCount} publishers agree across ${pairCount} compared pairs — ${discrepancyCount} discrepancies.`
            : `${publisherCount} publishers compared across ${pairCount} pairs do NOT agree — ${discrepancyCount} discrepanc${discrepancyCount === 1 ? 'y' : 'ies'}.`}
        </p>
      )}

      {basis?.expected && (
        <p className={styles.reconLead} data-testid="fov-recon-basis">
          <strong>Basis, checked on {basis.checkedCount ?? 0} surfaces:</strong> {basis.expected}
        </p>
      )}

      {fovWindow && (
        <p className={styles.reconLead} data-testid="fov-recon-window">
          Window {String(fovWindow.startIso).slice(0, 10)} → {String(fovWindow.endIso).slice(0, 10)}:{' '}
          <strong>{fovWindow.initialOrderCount ?? 0}</strong> initial of {fovWindow.orderCount ?? 0} orders,{' '}
          {fovWindow.firstOrderValueRowCount ?? 0} stored rows,{' '}
          {fovWindow.ordersWithoutFirstOrderValue ?? 0} without a First Order Value row.
        </p>
      )}

      <h3 className={styles.reconGroupTitle}>Publishers ({publisherCount})</h3>
      {missingPublishers.length > 0 && (
        <ul className={styles.reconCauses} data-testid="fov-recon-missing-publishers">
          {missingPublishers.map((key) => (
            <li key={key} className={styles.reconCause}>Missing publisher: {key}</li>
          ))}
        </ul>
      )}
      <ul className={styles.reconList} data-testid="fov-recon-publishers">
        {publishers.map((publisher) => (
          <PublisherRow key={publisher.key} publisher={publisher} reading={readings[publisher.key]} />
        ))}
      </ul>

      <h3 className={styles.reconGroupTitle}>Compared pairs ({pairCount})</h3>
      <ul className={styles.reconList} data-testid="fov-recon-pairs">
        {pairs.map((pair) => <PairRow key={`${pair.leftKey}--${pair.rightKey}`} pair={pair} />)}
      </ul>

      <h3 className={styles.reconGroupTitle}>Discrepancies ({discrepancyCount})</h3>
      {discrepancies.length === 0 ? (
        <p className={styles.reconGroupNote} data-testid="fov-recon-no-discrepancies">
          None. {pairCount} pairs were compared and every one matched — this is a measured zero, not
          an unrun check.
        </p>
      ) : (
        <ul className={styles.reconCauses} data-testid="fov-recon-discrepancies">
          {discrepancies.map((item, i) => (
            <li key={i} className={styles.reconCause}>
              {typeof item === 'string' ? item : JSON.stringify(item)}
            </li>
          ))}
        </ul>
      )}

      <RenewalsExcluded renewalsExcluded={renewalsExcluded} />
    </div>
  );
};

export default RevenueFovReconciliationPanel;
